import React, { useCallback, useEffect, useMemo, useState } from 'react';

const STATUS_OPTIONS = ['draft', 'active', 'archived'];
const AD_TYPE_OPTIONS = [
  { value: 'inline_link', label: 'Inline Link' },
  { value: 'activity_block', label: 'Activity Block' },
  { value: 'banner', label: 'Banner' },
];

const normalizeFolder = (value) => (value || '').replace(/^\/+|\/+$/g, '');

const getFolderEmoji = (name) => {
  const lower = String(name || '').toLowerCase();
  if (lower.includes('upload')) return '⬆️';
  if (lower.includes('photo')) return '📷';
  if (lower.includes('logo')) return '🧩';
  if (lower.includes('ai')) return '✨';
  if (lower.includes('announce')) return '📣';
  return '📁';
};

const THUMB_CACHE = new Map();

function AssetThumb({ asset }) {
  const [src, setSrc] = useState('');
  const [loading, setLoading] = useState(false);
  const requestUrl = asset.preview_api_url || asset.thumbnail_api_url || asset.thumbnail_url || '';

  useEffect(() => {
    let active = true;
    if (!requestUrl) {
      setSrc('');
      return () => {};
    }
    if (THUMB_CACHE.has(requestUrl)) {
      setSrc(THUMB_CACHE.get(requestUrl));
      return () => {};
    }
    const token = localStorage.getItem('token');
    const controller = new AbortController();
    setLoading(true);
    fetch(requestUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      cache: 'no-store',
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Thumb fetch failed (${res.status})`);
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        THUMB_CACHE.set(requestUrl, objectUrl);
        if (active) setSrc(objectUrl);
      })
      .catch(() => {
        if (active) setSrc('');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [requestUrl]);

  if (!requestUrl) return <div className="text-2xl">🖼️</div>;
  if (!src && loading) return <div className="text-sm text-gray-500">Loading…</div>;
  return src ? <img src={src} alt={asset.title || 'asset'} className="h-full w-full object-cover" /> : <div className="text-2xl">🖼️</div>;
}

function normalizeTagsText(tags) {
  if (!Array.isArray(tags)) return '';
  return tags.filter(Boolean).join(', ');
}

function parseTagsText(text) {
  if (!text) return [];
  return text
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function formatNumber(value) {
  const num = Number(value || 0);
  return Number.isFinite(num) ? num.toLocaleString() : '0';
}

function formatPercent(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return '0%';
  return `${(num * 100).toFixed(2)}%`;
}

function getAdTypeLabel(value) {
  return AD_TYPE_OPTIONS.find((option) => option.value === value)?.label || 'Banner';
}

function createEmptyForm() {
  return {
    adType: 'banner',
    title: '',
    body: '',
    imageId: '',
    imageIdStandard: '',
    imageIdLarge: '',
    ctaText: '',
    targetUrl: '',
    tagsText: '',
    tags: [],
    status: 'draft',
    manualWeight: 1,
  };
}

function requiresBody(adType) {
  return adType === 'banner';
}

function supportsInlineImage(adType) {
  return adType === 'banner' || adType === 'activity_block';
}

function supportsBannerImages(adType) {
  return adType === 'banner';
}

function supportsCtaText(adType) {
  return adType === 'banner' || adType === 'activity_block';
}

function TagInput({ value = [], suggestions = [], onChange }) {
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);

  const normalized = useMemo(() => value.map((tag) => tag.trim()).filter(Boolean), [value]);
  const uniqueSuggestions = useMemo(() => {
    const pool = new Set(suggestions.map((tag) => tag.trim()).filter(Boolean));
    normalized.forEach((tag) => pool.delete(tag));
    return Array.from(pool);
  }, [suggestions, normalized]);

  const visibleSuggestions = useMemo(() => {
    const q = draft.trim().toLowerCase();
    if (!q) return uniqueSuggestions.slice(0, 8);
    return uniqueSuggestions.filter((tag) => tag.toLowerCase().includes(q)).slice(0, 8);
  }, [draft, uniqueSuggestions]);

  const pushTag = useCallback((tag) => {
    const trimmed = tag.trim();
    if (!trimmed) return;
    if (normalized.includes(trimmed)) return;
    onChange?.([...normalized, trimmed]);
    setDraft('');
  }, [normalized, onChange]);

  const removeTag = useCallback((tag) => {
    onChange?.(normalized.filter((item) => item !== tag));
  }, [normalized, onChange]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      if (draft.trim()) pushTag(draft);
      return;
    }
    if (event.key === 'Backspace' && !draft) {
      removeTag(normalized[normalized.length - 1]);
    }
  };

  return (
    <div className="rounded border border-gray-700 bg-gray-900 px-3 py-2">
      <div className="flex flex-wrap gap-2">
        {normalized.map((tag) => (
          <span key={tag} className="flex items-center gap-2 rounded-full bg-gray-800 px-3 py-1 text-xs text-gray-200">
            {tag}
            <button
              type="button"
              className="text-gray-400 hover:text-white"
              onClick={() => removeTag(tag)}
              aria-label={`Remove ${tag}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          className="flex-1 min-w-[140px] bg-transparent text-sm text-white outline-none"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          onKeyDown={handleKeyDown}
          placeholder="Add tag and press Enter"
        />
      </div>
      {focused && visibleSuggestions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {visibleSuggestions.map((tag) => (
            <button
              key={tag}
              type="button"
              className="rounded-full border border-gray-700 px-3 py-1 text-xs text-gray-300 hover:border-gray-500 hover:text-white"
              onClick={() => pushTag(tag)}
            >
              + {tag}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TagFilter({ value = [], suggestions = [], onChange }) {
  const handleToggle = (tag) => {
    if (value.includes(tag)) {
      onChange(value.filter((item) => item !== tag));
    } else {
      onChange([...value, tag]);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {suggestions.map((tag) => (
        <button
          key={tag}
          type="button"
          className={`rounded-full px-3 py-1 text-xs ${
            value.includes(tag)
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
          onClick={() => handleToggle(tag)}
        >
          {tag}
        </button>
      ))}
    </div>
  );
}

function HtmlPreviewModal({ open, onClose, title, body, ctaText, imageSrc }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div
        className="max-w-3xl w-full rounded-lg border border-gray-800 bg-gray-950 p-5 text-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-lg font-semibold">HTML Preview</div>
            <div className="text-xs text-gray-400">{title || 'Untitled ad'}</div>
          </div>
          <button className="text-gray-400 hover:text-white text-xl" onClick={onClose} aria-label="Close preview">
            ×
          </button>
        </div>
        <div className="mt-4 rounded border border-gray-800 bg-gray-900/60 p-4">
          {title && <div className="mb-3 text-base font-semibold text-white">{title}</div>}
          {imageSrc && (
            <img
              src={imageSrc}
              alt={title || 'Ad preview'}
              className="mb-4 max-h-72 w-full rounded object-cover"
            />
          )}
          <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: body || '' }} />
          {ctaText && (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button type="button" className="rounded bg-indigo-600 px-3 py-2 text-sm text-white">
                {ctaText}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AssetPickerModal({ open, siteId, onClose, onSelect }) {
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState('');
  const [assets, setAssets] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadFolders = useCallback(async () => {
    if (!siteId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/assets?siteId=${encodeURIComponent(siteId)}&all=1&includeFolders=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load folders');
      setFolders(data.folders || []);
    } catch (err) {
      setError(err.message || 'Failed to load folders');
    }
  }, [siteId]);

  const loadAssets = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const folderParam = normalizeFolder(currentFolder);
      const url = `/api/assets?siteId=${encodeURIComponent(siteId)}&folder=${encodeURIComponent(folderParam)}&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load assets');
      setAssets(data.assets || []);
    } catch (err) {
      setError(err.message || 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [siteId, currentFolder, search]);

  useEffect(() => {
    if (open) {
      loadFolders();
      loadAssets();
    }
  }, [open, loadFolders, loadAssets]);

  const folderList = useMemo(() => {
    const normalized = folders.map(normalizeFolder).filter(Boolean);
    const unique = Array.from(new Set(normalized));
    return unique.sort((a, b) => a.localeCompare(b));
  }, [folders]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div
        className="w-full max-w-5xl rounded-lg border border-gray-800 bg-gray-950 p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="text-lg font-semibold">Select an Asset</div>
          <button className="text-gray-400 hover:text-white text-xl" onClick={onClose} aria-label="Close asset picker">
            ×
          </button>
        </div>
        <div className="mt-4 grid gap-4 lg:grid-cols-[220px,1fr]">
          <aside className="rounded border border-gray-800 bg-gray-900/60 p-3">
            <div className="text-xs uppercase tracking-widest text-gray-400 mb-3">Folders</div>
            <button
              className={`flex w-full items-center gap-2 rounded px-3 py-2 text-sm ${
                currentFolder === '' ? 'bg-indigo-600 text-white' : 'text-gray-200 hover:bg-gray-800'
              }`}
              onClick={() => setCurrentFolder('')}
            >
              🏠 Root
            </button>
            <div className="mt-3 space-y-1">
              {folderList.map((folder) => (
                <button
                  key={folder}
                  className={`flex w-full items-center gap-2 rounded px-3 py-2 text-sm ${
                    normalizeFolder(currentFolder) === folder ? 'bg-indigo-600 text-white' : 'text-gray-200 hover:bg-gray-800'
                  }`}
                  onClick={() => setCurrentFolder(folder)}
                >
                  <span>{getFolderEmoji(folder)}</span>
                  <span className="truncate">{folder}</span>
                </button>
              ))}
              {!folderList.length && <div className="text-xs text-gray-500">No folders yet.</div>}
            </div>
          </aside>

          <section className="rounded border border-gray-800 bg-gray-900/40 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search assets..."
                className="flex-1 min-w-[180px] rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500"
              />
            </div>
            {error && <div className="mt-3 text-sm text-rose-300">{error}</div>}
            {loading && <div className="mt-3 text-sm text-gray-400">Loading assets…</div>}
            {!loading && (
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {assets.map((asset) => (
                  <button
                    key={asset.id}
                    type="button"
                    className="rounded-lg border border-gray-800 bg-gray-950/70 p-3 text-left hover:border-indigo-500"
                    onClick={() => {
                      onSelect?.(asset);
                      onClose?.();
                    }}
                  >
                    <div className="h-28 w-full overflow-hidden rounded bg-gray-900 flex items-center justify-center">
                      <AssetThumb asset={asset} />
                    </div>
                    <div className="mt-2 text-sm font-semibold text-gray-100 truncate">
                      {asset.title || `Asset ${asset.id}`}
                    </div>
                    <div className="text-xs text-gray-500">{asset.folder || 'Root'}</div>
                  </button>
                ))}
                {!assets.length && (
                  <div className="text-sm text-gray-400">No assets in this folder.</div>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default function AdsManager({ siteId }) {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [creativeAssets, setCreativeAssets] = useState([]);
  const [editId, setEditId] = useState(null);
  const [filter, setFilter] = useState({
    q: '',
    status: 'all',
    tags: [],
    minImpressions: '',
    minCtr: '',
    maxCtr: '',
  });
  const [form, setForm] = useState({
    ...createEmptyForm(),
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState('inline');
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageStandard, setSelectedImageStandard] = useState(null);
  const [selectedImageLarge, setSelectedImageLarge] = useState(null);
  const selectedImageSrc = selectedImage
    ? selectedImage.thumbnail_url ||
      selectedImage.preview_api_url ||
      selectedImage.thumbnail_api_url ||
      selectedImage.thumbnailUrl ||
      ''
    : '';
  const currentAdType = form.adType || 'banner';

  const assetById = useMemo(() => {
    const map = new Map();
    (Array.isArray(creativeAssets) ? creativeAssets : []).forEach((asset) => {
      if (asset?.id) map.set(String(asset.id), asset);
    });
    return map;
  }, [creativeAssets]);

  const allTags = useMemo(() => {
    const set = new Set();
    ads.forEach((ad) => {
      if (Array.isArray(ad.tags)) {
        ad.tags.forEach((tag) => set.add(tag));
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [ads]);

  const filteredAds = useMemo(() => {
    const q = filter.q.trim().toLowerCase();
    const minImpressions = Number(filter.minImpressions || 0);
    const minCtr = filter.minCtr === '' ? null : Number(filter.minCtr);
    const maxCtr = filter.maxCtr === '' ? null : Number(filter.maxCtr);
    return ads.filter((ad) => {
      const matchesStatus = filter.status === 'all' || ad.status === filter.status;
      const haystack = `${ad.title || ''} ${ad.body || ''}`.toLowerCase();
      const matchesQuery = !q || haystack.includes(q);
      const matchesTags =
        filter.tags.length === 0 ||
        (Array.isArray(ad.tags) && filter.tags.every((tag) => ad.tags.includes(tag)));
      const impressions = Number(ad.impressions_cached || 0);
      const ctr = Number(ad.ctr_cached || 0);
      const matchesImpressions = impressions >= minImpressions;
      const matchesMinCtr = minCtr === null || ctr >= minCtr / 100;
      const matchesMaxCtr = maxCtr === null || ctr <= maxCtr / 100;
      return (
        matchesStatus &&
        matchesQuery &&
        matchesTags &&
        matchesImpressions &&
        matchesMinCtr &&
        matchesMaxCtr
      );
    });
  }, [ads, filter]);

  const loadCreativeAssets = useCallback(async () => {
    if (!siteId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/creative/assets?siteId=${encodeURIComponent(siteId)}`, {
        cache: 'no-store',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setCreativeAssets(Array.isArray(data.assets) ? data.assets : []);
    } catch (err) {
      console.error('Failed to load creative assets for ads', err);
      setCreativeAssets([]);
    }
  }, [siteId]);

  const loadAds = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/creative/ads', {
        cache: 'no-store',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(`Failed to load ads (${res.status})`);
      }
      const data = await res.json();
      setAds(Array.isArray(data.ads) ? data.ads : []);
    } catch (err) {
      console.error('Failed to load ads', err);
      setError('Failed to load ads.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCreativeAssets();
  }, [loadCreativeAssets]);

  useEffect(() => {
    if (!editId) return;
    if (form.imageId) {
      const asset = assetById.get(String(form.imageId));
      if (asset) setSelectedImage(asset);
    }
    if (form.imageIdStandard) {
      const asset = assetById.get(String(form.imageIdStandard));
      if (asset) setSelectedImageStandard(asset);
    }
    if (form.imageIdLarge) {
      const asset = assetById.get(String(form.imageIdLarge));
      if (asset) setSelectedImageLarge(asset);
    }
  }, [assetById, editId, form.imageId, form.imageIdStandard, form.imageIdLarge]);

  const resetForm = useCallback(() => {
    setEditId(null);
    setForm(createEmptyForm());
    setSelectedImage(null);
    setSelectedImageStandard(null);
    setSelectedImageLarge(null);
  }, []);

  const handleEdit = (ad) => {
    setEditId(ad.id);
    setForm({
      adType: ad.ad_type || 'banner',
      title: ad.title || '',
      body: ad.body || '',
      imageId: ad.image_id ? String(ad.image_id) : '',
      imageIdStandard: ad.image_id_standard ? String(ad.image_id_standard) : '',
      imageIdLarge: ad.image_id_large ? String(ad.image_id_large) : '',
      ctaText: ad.cta_text || '',
      targetUrl: ad.target_url || '',
      tagsText: normalizeTagsText(ad.tags),
      tags: Array.isArray(ad.tags) ? ad.tags : [],
      status: ad.status || 'draft',
      manualWeight: ad.manual_weight || 1,
    });
    setSelectedImage(ad.image_id ? (assetById.get(String(ad.image_id)) || {
      id: ad.image_id,
      title: `Asset ${ad.image_id}`,
      thumbnail_url: null,
    }) : null);
    setSelectedImageStandard(ad.image_id_standard ? (assetById.get(String(ad.image_id_standard)) || {
      id: ad.image_id_standard,
      title: `Asset ${ad.image_id_standard}`,
      thumbnail_url: null,
    }) : null);
    setSelectedImageLarge(ad.image_id_large ? (assetById.get(String(ad.image_id_large)) || {
      id: ad.image_id_large,
      title: `Asset ${ad.image_id_large}`,
      thumbnail_url: null,
    }) : null);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }
    if (requiresBody(currentAdType) && !form.body.trim()) {
      setError('Body is required for banners.');
      return;
    }
    if (!form.targetUrl.trim()) {
      setError('Target URL is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        adType: currentAdType,
        title: form.title.trim(),
        body: form.body,
        imageId: supportsInlineImage(currentAdType) && form.imageId ? Number(form.imageId) : null,
        imageIdStandard: supportsBannerImages(currentAdType) && form.imageIdStandard ? Number(form.imageIdStandard) : null,
        imageIdLarge: supportsBannerImages(currentAdType) && form.imageIdLarge ? Number(form.imageIdLarge) : null,
        ctaText: supportsCtaText(currentAdType) ? (form.ctaText.trim() || null) : null,
        targetUrl: form.targetUrl.trim() || null,
        tags: Array.isArray(form.tags) && form.tags.length ? form.tags : parseTagsText(form.tagsText),
        status: form.status,
        manualWeight: Number(form.manualWeight) || 1,
      };
      const endpoint = editId ? `/api/creative/ads/${editId}` : '/api/creative/ads';
      const method = editId ? 'PUT' : 'POST';
      const res = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || `Failed to save ad (${res.status})`);
      }
      const data = await res.json();
      if (editId) {
        setAds((prev) => prev.map((ad) => (ad.id === editId ? data.ad : ad)));
        setEditId(data.ad.id);
      } else {
        setAds((prev) => [data.ad, ...prev]);
        setEditId(data.ad.id);
      }
      setForm({
        adType: data.ad.ad_type || currentAdType,
        title: data.ad.title || '',
        body: data.ad.body || '',
        imageId: data.ad.image_id ? String(data.ad.image_id) : '',
        imageIdStandard: data.ad.image_id_standard ? String(data.ad.image_id_standard) : '',
        imageIdLarge: data.ad.image_id_large ? String(data.ad.image_id_large) : '',
        ctaText: data.ad.cta_text || '',
        targetUrl: data.ad.target_url || '',
        tagsText: normalizeTagsText(data.ad.tags),
        tags: Array.isArray(data.ad.tags) ? data.ad.tags : [],
        status: data.ad.status || 'draft',
        manualWeight: data.ad.manual_weight || 1,
      });
      if (data.ad.image_id) {
        if (!selectedImage || String(selectedImage.id) !== String(data.ad.image_id)) {
          setSelectedImage({
            id: data.ad.image_id,
            title: `Asset ${data.ad.image_id}`,
            thumbnail_url: null,
          });
        }
      } else {
        setSelectedImage(null);
      }
      if (data.ad.image_id_standard) {
        if (!selectedImageStandard || String(selectedImageStandard.id) !== String(data.ad.image_id_standard)) {
          setSelectedImageStandard({
            id: data.ad.image_id_standard,
            title: `Asset ${data.ad.image_id_standard}`,
            thumbnail_url: null,
          });
        }
      } else {
        setSelectedImageStandard(null);
      }
      if (data.ad.image_id_large) {
        if (!selectedImageLarge || String(selectedImageLarge.id) !== String(data.ad.image_id_large)) {
          setSelectedImageLarge({
            id: data.ad.image_id_large,
            title: `Asset ${data.ad.image_id_large}`,
            thumbnail_url: null,
          });
        }
      } else {
        setSelectedImageLarge(null);
      }
    } catch (err) {
      console.error('Failed to save ad', err);
      setError('Failed to save ad.');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (adId) => {
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/creative/ads/${adId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(`Failed to archive ad (${res.status})`);
      }
      setAds((prev) =>
        prev.map((ad) => (ad.id === adId ? { ...ad, status: 'archived' } : ad))
      );
    } catch (err) {
      console.error('Failed to archive ad', err);
      setError('Failed to archive ad.');
    }
  };

  useEffect(() => {
    loadAds();
  }, [loadAds]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-semibold">Ads</h2>
        <button
          className="px-3 py-1 rounded bg-gray-700 text-white"
          onClick={loadAds}
          disabled={loading}
        >
          Refresh
        </button>
        <button
          className="px-3 py-1 rounded bg-indigo-600 text-white"
          onClick={resetForm}
        >
          New Ad
        </button>
      </div>

      {error && <div className="text-red-400">{error}</div>}

      <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-lg font-semibold">{editId ? 'Edit Ad' : 'Create Ad'}</h3>
            <button
              className="px-3 py-1 rounded bg-indigo-600 text-white text-sm disabled:opacity-50"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
          <div className="grid gap-3">
            <label className="text-sm text-gray-300">
              Ad Type
              <select
                className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                value={currentAdType}
                onChange={(e) => {
                  const nextType = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    adType: nextType,
                    imageIdStandard: supportsBannerImages(nextType) ? prev.imageIdStandard : '',
                    imageIdLarge: supportsBannerImages(nextType) ? prev.imageIdLarge : '',
                    ctaText: supportsCtaText(nextType) ? prev.ctaText : '',
                  }));
                  if (!supportsBannerImages(nextType)) {
                    setSelectedImageStandard(null);
                    setSelectedImageLarge(null);
                  }
                }}
              >
                {AD_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-300">
              Title
              <input
                className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Ad title"
              />
            </label>
            <label className="text-sm text-gray-300">
              {currentAdType === 'banner' ? 'Body' : 'Description'}
              <textarea
                className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white h-28"
                value={form.body}
                onChange={(e) => setForm((prev) => ({ ...prev, body: e.target.value }))}
                placeholder={
                  currentAdType === 'inline_link'
                    ? 'Optional short description'
                    : currentAdType === 'activity_block'
                      ? 'Short activity description'
                      : 'Ad description or HTML'
                }
              />
            </label>
            {supportsInlineImage(currentAdType) && (
              <div className="space-y-3">
              <label className="text-sm text-gray-300">
                {currentAdType === 'activity_block' ? 'Card Image' : 'Inline Image (300 × 250)'}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="rounded bg-gray-800 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700"
                    onClick={() => {
                      setPickerTarget('inline');
                      setPickerOpen(true);
                    }}
                  >
                    Choose Asset
                  </button>
                  {form.imageId && (
                    <button
                      type="button"
                      className="rounded bg-gray-800 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700"
                      onClick={() => {
                        setForm((prev) => ({ ...prev, imageId: '' }));
                        setSelectedImage(null);
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </label>
              {selectedImage && (
                selectedImage.thumbnail_url ||
                selectedImage.preview_api_url ||
                selectedImage.thumbnail_api_url ||
                selectedImage.thumbnailUrl ? (
                  <img
                    src={selectedImage.thumbnail_url || selectedImage.preview_api_url || selectedImage.thumbnail_api_url || selectedImage.thumbnailUrl || ''}
                    alt="Selected inline ad"
                    className="max-h-48 rounded border border-gray-700 object-cover"
                  />
                ) : (
                  <div className="rounded border border-gray-700 bg-gray-900 p-3 text-sm text-gray-300">
                    Selected asset #{selectedImage.id}
                  </div>
                )
              )}
              {supportsBannerImages(currentAdType) && (
                <>
              <label className="text-sm text-gray-300">
                Standard Banner (320 × 50)
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="rounded bg-gray-800 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700"
                    onClick={() => {
                      setPickerTarget('standard');
                      setPickerOpen(true);
                    }}
                  >
                    Choose Asset
                  </button>
                  {form.imageIdStandard && (
                    <button
                      type="button"
                      className="rounded bg-gray-800 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700"
                      onClick={() => {
                        setForm((prev) => ({ ...prev, imageIdStandard: '' }));
                        setSelectedImageStandard(null);
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </label>
              {selectedImageStandard && (
                selectedImageStandard.thumbnail_url ||
                selectedImageStandard.preview_api_url ||
                selectedImageStandard.thumbnail_api_url ||
                selectedImageStandard.thumbnailUrl ? (
                  <img
                    src={selectedImageStandard.thumbnail_url || selectedImageStandard.preview_api_url || selectedImageStandard.thumbnail_api_url || selectedImageStandard.thumbnailUrl || ''}
                    alt="Selected standard banner"
                    className="max-h-24 rounded border border-gray-700 object-cover"
                  />
                ) : (
                  <div className="rounded border border-gray-700 bg-gray-900 p-3 text-sm text-gray-300">
                    Selected asset #{selectedImageStandard.id}
                  </div>
                )
              )}

              <label className="text-sm text-gray-300">
                Large Banner (320 × 100)
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="rounded bg-gray-800 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700"
                    onClick={() => {
                      setPickerTarget('large');
                      setPickerOpen(true);
                    }}
                  >
                    Choose Asset
                  </button>
                  {form.imageIdLarge && (
                    <button
                      type="button"
                      className="rounded bg-gray-800 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700"
                      onClick={() => {
                        setForm((prev) => ({ ...prev, imageIdLarge: '' }));
                        setSelectedImageLarge(null);
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </label>
              {selectedImageLarge && (
                selectedImageLarge.thumbnail_url ||
                selectedImageLarge.preview_api_url ||
                selectedImageLarge.thumbnail_api_url ||
                selectedImageLarge.thumbnailUrl ? (
                  <img
                    src={selectedImageLarge.thumbnail_url || selectedImageLarge.preview_api_url || selectedImageLarge.thumbnail_api_url || selectedImageLarge.thumbnailUrl || ''}
                    alt="Selected large banner"
                    className="max-h-28 rounded border border-gray-700 object-cover"
                  />
                ) : (
                  <div className="rounded border border-gray-700 bg-gray-900 p-3 text-sm text-gray-300">
                    Selected asset #{selectedImageLarge.id}
                  </div>
                )
              )}
                </>
              )}
              </div>
            )}
            {supportsCtaText(currentAdType) && (
            <label className="text-sm text-gray-300">
              CTA Text
              <input
                className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                value={form.ctaText}
                onChange={(e) => setForm((prev) => ({ ...prev, ctaText: e.target.value }))}
                placeholder="Learn more"
              />
            </label>
            )}
            <label className="text-sm text-gray-300">
              Target URL
              <input
                className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                value={form.targetUrl}
                onChange={(e) => setForm((prev) => ({ ...prev, targetUrl: e.target.value }))}
                placeholder="https://example.com"
              />
            </label>
            <label className="text-sm text-gray-300">
              Tags
              <div className="mt-1">
                <TagInput
                  value={form.tags}
                  suggestions={allTags}
                  onChange={(tags) => setForm((prev) => ({ ...prev, tags, tagsText: normalizeTagsText(tags) }))}
                />
              </div>
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm text-gray-300">
                Status
                <select
                  className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                  value={form.status}
                  onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm text-gray-300">
                Manual Weight
                <input
                  type="number"
                  min="1"
                  className="mt-1 w-full rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                  value={form.manualWeight}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, manualWeight: e.target.value }))
                  }
                />
              </label>
            </div>
            <div className="flex gap-2">
              <button
                className="px-4 py-2 rounded bg-indigo-600 text-white disabled:opacity-50"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : editId ? 'Save Changes' : 'Create Ad'}
              </button>
              {editId && (
                <button
                  className="px-4 py-2 rounded bg-gray-700 text-white"
                  onClick={resetForm}
                >
                  Cancel
                </button>
              )}
            </div>
            <div className="rounded-lg border border-gray-800 bg-gray-950/60 p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-gray-200">Live Preview</div>
                <button
                  className="text-xs text-gray-400 hover:text-white"
                  type="button"
                  onClick={() => setPreviewOpen(true)}
                >
                  Expand
                </button>
              </div>
              <div className="mt-3 rounded border border-gray-800 bg-gray-950/60 p-4">
                {form.title && <div className="mb-3 text-base font-semibold text-white">{form.title}</div>}
                {supportsInlineImage(currentAdType) && selectedImage && (
                  selectedImageSrc ? (
                    <img
                      src={selectedImageSrc}
                      alt={form.title || 'Selected ad'}
                      className="mb-4 max-h-56 w-full rounded object-cover"
                    />
                  ) : (
                    <div className="mb-4 rounded border border-gray-700 bg-gray-900 p-3 text-sm text-gray-300">
                      Selected asset #{selectedImage.id}
                    </div>
                  )
                )}
                <div
                  className="prose prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: form.body || '' }}
                />
                {supportsCtaText(currentAdType) && form.ctaText && (
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <button type="button" className="rounded bg-indigo-600 px-3 py-2 text-sm text-white">
                      {form.ctaText}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
          <h3 className="text-lg font-semibold mb-3">Ad Library</h3>
          <div className="flex flex-wrap gap-3 mb-4">
            <input
              className="flex-1 min-w-[200px] rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
              placeholder="Search ads"
              value={filter.q}
              onChange={(e) => setFilter((prev) => ({ ...prev, q: e.target.value }))}
            />
            <select
              className="rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
              value={filter.status}
              onChange={(e) => setFilter((prev) => ({ ...prev, status: e.target.value }))}
            >
              <option value="all">All statuses</option>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            <input
              className="w-28 rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
              placeholder="Min Impr."
              type="number"
              min="0"
              value={filter.minImpressions}
              onChange={(e) => setFilter((prev) => ({ ...prev, minImpressions: e.target.value }))}
            />
            <input
              className="w-24 rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
              placeholder="CTR % min"
              type="number"
              min="0"
              step="0.1"
              value={filter.minCtr}
              onChange={(e) => setFilter((prev) => ({ ...prev, minCtr: e.target.value }))}
            />
            <input
              className="w-24 rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
              placeholder="CTR % max"
              type="number"
              min="0"
              step="0.1"
              value={filter.maxCtr}
              onChange={(e) => setFilter((prev) => ({ ...prev, maxCtr: e.target.value }))}
            />
          </div>
          {allTags.length > 0 && (
            <div className="mb-4">
              <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Filter Tags</div>
              <TagFilter
                value={filter.tags}
                suggestions={allTags}
                onChange={(tags) => setFilter((prev) => ({ ...prev, tags }))}
              />
            </div>
          )}
          {loading && <div className="text-gray-400">Loading ads...</div>}
          {!loading && filteredAds.length === 0 && (
            <div className="text-gray-400">No ads yet.</div>
          )}
          <div className="space-y-3">
            {filteredAds.map((ad) => (
              <div
                key={ad.id}
                className="rounded border border-gray-800 bg-gray-950/60 p-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="text-sm text-gray-400">#{ad.id}</div>
                    <div className="text-base font-semibold">{ad.title}</div>
                    <div className="text-xs text-gray-400">{getAdTypeLabel(ad.ad_type)} • {ad.status}</div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="px-2 py-1 rounded bg-gray-700 text-white text-sm"
                      onClick={() => handleEdit(ad)}
                    >
                      Edit
                    </button>
                    {ad.status !== 'archived' && (
                      <button
                        className="px-2 py-1 rounded bg-red-700 text-white text-sm"
                        onClick={() => handleArchive(ad.id)}
                      >
                        Archive
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-2 text-sm text-gray-300 line-clamp-2">
                  {ad.body || ad.target_url}
                </div>
                <div className="mt-2 text-xs text-gray-400">
                  Impressions: {formatNumber(ad.impressions_cached)} • Clicks:{' '}
                  {formatNumber(ad.clicks_cached)} • CTR: {formatPercent(ad.ctr_cached)}
                </div>
                {Array.isArray(ad.tags) && ad.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {ad.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 rounded-full bg-gray-800 text-xs text-gray-200"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <HtmlPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={form.title}
        body={form.body}
        ctaText={form.ctaText}
        imageSrc={selectedImageSrc}
      />
      <AssetPickerModal
        open={pickerOpen}
        siteId={siteId}
        onClose={() => setPickerOpen(false)}
        onSelect={(asset) => {
          if (pickerTarget === 'standard') {
            setSelectedImageStandard(asset);
            setForm((prev) => ({ ...prev, imageIdStandard: asset?.id ? String(asset.id) : '' }));
          } else if (pickerTarget === 'large') {
            setSelectedImageLarge(asset);
            setForm((prev) => ({ ...prev, imageIdLarge: asset?.id ? String(asset.id) : '' }));
          } else {
            setSelectedImage(asset);
            setForm((prev) => ({ ...prev, imageId: asset?.id ? String(asset.id) : '' }));
          }
        }}
      />
    </div>
  );
}
